<template>
  <content-modal
    class="wait-modal async-load"
    :visible="visible"
    :close-aria-label="'Close'"
    :size="'large'"
    :use-state-button="true"
    :auto-hide-off="true"
    :show-header="false"
    @modal-hide-request="handleHideRequest"
    @modal-hidden="handleHide"
  >
    <template #content>
      <modal-content>
        <div class="graphic" :class="{ _loading: isProcessing }">
          <cat-chasing-ball v-if="isProcessing" />
          <falling-ball-success v-if="isComplete" />
        </div>
        <h3 class="title">{{ currentTitle }}</h3>
        <p v-safe-html="currentContent" class="message" />
        <!-- (optional) for wait modals that have url link as part of its completed page-->
        <div v-if="copyUrlLink && copyUrlTooltipMessage" class="copy-url">
          <code-snippet kind="oneline" :copy-feedback="copyUrlTooltipMessage">
            <ellipsis-text :text="copyUrlLink" />
          </code-snippet>
        </div>
        <div v-if="showTools" class="tools">
          <cv-button
            v-if="primaryButtonText"
            type="button"
            kind="primary"
            size="field"
            @click="handlePrimaryClick"
          >
            {{ primaryButtonText }}
          </cv-button>
          <cv-button
            v-if="secondaryButtonText"
            type="button"
            kind="tertiary"
            size="field"
            @click="handleSecondaryClick"
          >
            {{ secondaryButtonText }}
          </cv-button>
        </div>
        <!-- Optional content rendered after the action buttons (e.g. upvote prompt card) -->
        <slot name="after-tools" />
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import modalEventBus from "@plugins/modal/service/event-bus.ts";
import { computed, ref, onMounted, getCurrentInstance } from "vue";
import CatChasingBall from "@images/async-loading/cat-chasing-ball.svg?component";
import FallingBallSuccess from "@images/async-loading/falling-ball-success.svg?component";
import { isEmpty } from "@utils/var.check.ts";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import CodeSnippet from "@plugins/common/components/CodeSnippet.vue";
interface StateMessage {
  title: string;
  content: string;
}

interface Props {
  params: {
    state: "processing" | "complete" | "error";
    stateMessage: {
      processing: StateMessage;
      complete: StateMessage;
      error: StateMessage;
    };
    copyUrl?: {
      link: string;
      tooltipMessage: string;
    };
    primaryButtonText?: string;
    secondaryButtonText?: string;
  };
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "action-primary": [];
  "action-secondary": [];
}>();

const visible = ref(true);

const isProcessing = computed(() => props.params.state === "processing");
const isComplete = computed(() => props.params.state === "complete");
const isError = computed(() => props.params.state === "error");

const currentTitle = computed(() => {
  if (isProcessing.value) return props.params.stateMessage.processing.title;
  if (isComplete.value) return props.params.stateMessage.complete.title;
  if (isError.value) return props.params.stateMessage.error.title;
  return "";
});

const currentContent = computed(() => {
  if (isProcessing.value) return props.params.stateMessage.processing.content;
  if (isComplete.value) return props.params.stateMessage.complete.content;
  if (isError.value) return props.params.stateMessage.error.content;
  return "";
});

const copyUrlLink = computed(() => props.params.copyUrl?.link ?? "");
const copyUrlTooltipMessage = computed(() => props.params.copyUrl?.tooltipMessage ?? "");

const primaryButtonText = computed(() => props.params?.primaryButtonText ?? "");
const secondaryButtonText = computed(() => props.params?.secondaryButtonText ?? "");

const showTools = computed(() => {
  return !isEmpty(primaryButtonText.value) || !isEmpty(secondaryButtonText.value);
});

onMounted(() => {
  visible.value = true;
});

const handleHideRequest = (event: object) => {
  if (event["cv:reason"] !== "external-click") {
    visible.value = false;
  }
};

const vnode = getCurrentInstance()?.vnode.key;
const handleHide = () => {
  modalEventBus.$emit("modal-hidden", vnode);
};

const handlePrimaryClick = () => {
  emit("action-primary");
};

const handleSecondaryClick = () => {
  emit("action-secondary");
};
</script>
